import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterOptions } from 'app/models/DropDown';
import { IExceptionWaive } from '../Content/Requirements/DialogComponents/models/PlacementDialogModels';

export enum PlacementGridType {
  events = 'events',
  requirements = 'requirements',
}
export interface IPlacementEvent {
  rowID?: number;
  placementID?: number;
  eventID?: number;
  eventTypeID?: string;
  eventType?: string;
  statusID?: number;
  status?: string;
  followUpDate?: string;
  followUpDateNoFormat?: string;
  dateEntered?: string;
  lastUpdatedDate?: string;
  lastUpdatedDateNoFormat?: string;
  lastUpdatedBy?: string;
  lastUpdatedByLastName: string;
  statusChangedDate?: string;
  notes?: string;
  statusChangedByUserID?: number;
  enteredByUserID?: number;
  changedName?: {
    changedByFirstName?: string;
    changedByLastName?: string;
  };
  enterdName?: {
    enteredByFirstName?: string;
    enteredByLastName?: string;
  };
  timestamp?: string;
}

export interface IPlacementRequirementFilters {
  letterResourceOpt: [];
  requirementSourceOpt: [];
  statusOpt: [];
}
export interface IPlacementRequirement {
  rowID?: number;
  placementID?: number;
  requirementTypeID?: number;
  requirementType?: string;
  requirementTypeFull?: string;
  category?: string;
  credentialName?: string;
  credentialCategory?: string;
  checkListID?: number;
  complianceLetterTypeID?: number;
  requesterTypeId?: number;
  description?: string;
  statusID?: number;
  status?: string;
  dateEntered?: string | null;
  dateReceived?: string | null;
  dateReceivedNoFormat?: string;
  dateFaxed?: string | null;
  dateFaxedNoFormat?: string;
  lastUpdatedDate?: string | null;
  lastUpdatedDateNoFormat?: string;
  lastUpdatedBy?: string;
  lastUpdatedByLastName?: string;
  requirementSourceId?: number;
  requirementSource?: string;
  requirementSourceFilterOptions?: any[];
  letterResourceId?: number;
  letterResource?: string;
  letterSource?: string;
  letterSourceFilterOptions?: any[];
  letterSourceControl?: string;
  statusChangedDate?: string;
  descriptionHoverViewData?: boolean | any[];
  notes?: string;
  statusChangedByUserID?: number;
  enteredByUserID?: number;
  changedName?: {
    changedByFirstName?: string;
    changedByLastName?: string;
  };
  enterdName?: {
    enteredByFirstName?: string;
    enteredByLastName?: string;
  };
  timestamp?: string;
  exceptionWaiveColumns?: any[];
  exceptionWaiveRows?: any[] | null;
  expandable?: boolean;
  exceptionWaive?: boolean;
}

export interface IPlacementEventDetails {
  events: IPlacementEvent[];
  requirements: IPlacementRequirement[];
  types: FilterOptions[];
  selectedCategory: any | null;
  selectedPlacementGrid: PlacementGridType;
  loading: boolean;
  loadingDialog: boolean;
  error: boolean;
  checkList?: IPlacementRequirementFilters;
  reloadData: boolean;
  isSuccess: boolean;
  exceptionWave: IExceptionWaive | null;
  exceptionWaveNewAttachment: any[] | null;
  deletedAttachments: any[] | null;
  AttachmentItemState: any;
  AttachmentErrorMessage: any;
  Notify: boolean;
  exceptionDialogState: any;
}

export const initialState = {
  events: [],
  requirements: [],
  types: [],
  selectedCategory: null,
  selectedPlacementGrid: PlacementGridType.events,
  loading: true,
  loadingDialog: true,
  error: false,
  checkList: undefined,
  reloadData: false,
  isSuccess: false,
  exceptionWave: null,
  exceptionWaveNewAttachment: null,
  exceptionWaveExistingAttachment: null,
  deletedAttachments: null,
  AttachmentItemState: null,
  AttachmentErrorMessage: null,
  Notify: false,
  exceptionDialogState: {
    open: false,
    add: false,
    edit: false,
    credential: false,
  },
} as IPlacementEventDetails;

const getPlacementEventsAction = createAction<{
  placementId: number;
}>('GET_PLACEMENT_EVENTS');

const addRequirementAction = createAction<IPlacementRequirement>('ADD_REQUIREMENTS');

const getRequirementCheckListAction = createAction<any>('GET_REQUIREMENTS_CHECKLIST');

const placementEventsSlice = createSlice({
  name: 'placementEventDetails',
  initialState: initialState,
  reducers: {
    setPlacementEvent(state, action: PayloadAction<IPlacementEvent[]>) {
      state.events = action.payload;
    },
    setPlacementRequirement(state, action: PayloadAction<IPlacementRequirement[]>) {
      state.requirements = action.payload;
    },
    setPlacementEventTypes(state, action: PayloadAction<FilterOptions[]>) {
      state.types = action.payload;
    },
    setSelectedCategory(state, action: PayloadAction<any | null>) {
      state.selectedCategory = action.payload;
    },
    setSelectedPlacementGrid(state, action: PayloadAction<PlacementGridType>) {
      state.selectedPlacementGrid = action.payload;
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setLoadingDialog(state, action: PayloadAction<boolean>) {
      state.loadingDialog = action.payload;
    },
    setError(state, action: PayloadAction<boolean>) {
      state.error = action.payload;
    },
    setCheckListData(state, action: PayloadAction<IPlacementRequirementFilters>) {
      state.checkList = action.payload;
    },
    setReload(state, action: PayloadAction<boolean>) {
      state.reloadData = !state.reloadData;
    },
    setIsSuccess(state, action: PayloadAction<boolean>) {
      state.isSuccess = action.payload;
    },
    setExceptionWaive(state, action: PayloadAction<IExceptionWaive | null>) {
      state.exceptionWave = action.payload;
    },
    setExceptionWaveNewAttachment(state, action: PayloadAction<any[] | null>) {
      state.exceptionWaveNewAttachment = action.payload;
    },
    appendExceptionWaveNewAttachment(state, action: PayloadAction<any>) {
      state.exceptionWaveNewAttachment = [...(state.exceptionWaveNewAttachment || []), action.payload];
    },
    setDeletedAttachment(state, action: PayloadAction<any[] | null>) {
      state.deletedAttachments = action.payload;
    },
    appendDeletedAttachments(state, action: PayloadAction<any[] | null>) {
      state.deletedAttachments = [...(state.deletedAttachments || []), action.payload];
    },
    setAttachmentItemState(state, action: PayloadAction<any>) {
      state.AttachmentItemState = action.payload;
    },
    setAttachmentErrorMessage(state, action: PayloadAction<any>) {
      state.AttachmentErrorMessage = action.payload;
    },
    setNotify(state, action: PayloadAction<boolean>) {
      state.Notify = action.payload;
    },
    setExceptionDialogState(state, action: PayloadAction<any>) {
      state.exceptionDialogState = action.payload;
    },
    reset() {
      return {
        ...initialState,
      };
    },
  },
});

const { actions } = placementEventsSlice;

export const { reducer: placementEventsReducer, name: placementEventsSliceKey } = placementEventsSlice;
export const placementEventActions = {
  ...actions,
  getPlacementEventsAction,
  addRequirementAction,
  getRequirementCheckListAction,
};
